import React, { useState } from 'react';
import { View, type LayoutChangeEvent } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../src/theme/useTheme';
import { buildSketchBorderPath } from './sketchBorderPath.helper';
import { getSketchFrameStyles } from './SketchFrame.styles';

export interface SketchFrameProps {
  children: React.ReactNode;
  /** Corner radius to trace — match the wrapped card's radius. */
  radius?: number;
  /** Varies the wobble so stacked frames don't look identical. */
  seed?: number;
}

interface FrameSize {
  width: number;
  height: number;
}

/**
 * Inks a hand-drawn (Design B) border around its child. The border is a computed
 * wobbly `<Path>` measured to the child via `onLayout` — two overlaid strokes give
 * the sketched double-line feel. Decorative only; never intercepts touches.
 */
export const SketchFrame: React.FC<SketchFrameProps> = ({
  children,
  radius = 24,
  seed = 1,
}) => {
  const theme = useTheme();
  const styles = getSketchFrameStyles();
  const [size, setSize] = useState<FrameSize | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setSize((prev) =>
      prev && prev.width === width && prev.height === height ? prev : { width, height },
    );
  };

  const hasSize = size !== null && size.width > 0 && size.height > 0;

  return (
    <View style={styles.frame} onLayout={handleLayout}>
      {children}
      {hasSize ? (
        <Svg
          style={styles.border}
          width={size.width}
          height={size.height}
          pointerEvents="none"
        >
          <Path
            d={buildSketchBorderPath({ width: size.width, height: size.height, radius, seed })}
            stroke={theme.primaryLight}
            strokeWidth={1.7}
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
          />
          <Path
            d={buildSketchBorderPath({
              width: size.width,
              height: size.height,
              radius,
              seed: seed + 1,
            })}
            stroke={theme.primaryLight}
            strokeWidth={1.2}
            strokeLinejoin="round"
            strokeLinecap="round"
            fill="none"
            opacity={0.45}
          />
        </Svg>
      ) : null}
    </View>
  );
};
